import type { InventoryStrategy, MediaStrategy, PriceStrategy } from "@/lib/channels";
import { buildPricingProviderConfig, computeChannelPrice, sellingPriceError } from "@/lib/listing-pricing";
import {
  isCanonicalMediaStrategy,
  mediaStrategyRequiresPrimaryAsset,
  toCanonicalMediaStrategy,
} from "@/lib/media-strategy";

export type ChannelWorkspaceDraft = {
  connectorId: string;
  selected: boolean;
  channelTitle: string;
  channelDescription: string;
  categoryId: string;
  categoryName: string;
  categoryConfirmed: boolean;
  priceStrategy: PriceStrategy;
  priceValue: string;
  inventoryStrategy: InventoryStrategy;
  inventoryCap: string;
  inventoryPercent: string;
  mediaStrategy: MediaStrategy;
  primaryMediaAssetId: string;
  variantMode: "all" | "selected";
  variantIds: string[];
};

export type ChannelDraftIssue = {
  connectorId: string;
  field: string;
  message: string;
};

export function emptyChannelDraft(connectorId: string, productName: string, description: string, allVariantIds: string[]): ChannelWorkspaceDraft {
  return {
    connectorId,
    selected: true,
    channelTitle: productName,
    channelDescription: description,
    categoryId: "",
    categoryName: "",
    categoryConfirmed: false,
    priceStrategy: "inherit",
    priceValue: "",
    inventoryStrategy: "all_available",
    inventoryCap: "",
    inventoryPercent: "",
    mediaStrategy: "inherit",
    primaryMediaAssetId: "",
    variantMode: "all",
    variantIds: [...allVariantIds],
  };
}

export function cloneDraftIndependent(source: ChannelWorkspaceDraft, connectorId: string): ChannelWorkspaceDraft {
  return { ...source, connectorId, variantIds: [...source.variantIds] };
}

export function calculatedDraftPrice(draft: ChannelWorkspaceDraft, basePrice: number): number {
  const n = draft.priceValue.trim() === "" ? null : Number(draft.priceValue);
  return computeChannelPrice(draft.priceStrategy, basePrice, {
    percentageMarkup: draft.priceStrategy === "percentage_markup" ? n : null,
    fixedMarkup: draft.priceStrategy === "fixed_markup" ? n : null,
    fixedOverride: draft.priceStrategy === "fixed_override" ? n : null,
  });
}

export function validateChannelDraft(
  draft: ChannelWorkspaceDraft,
  opts: { basePrice: number; requireCategory?: boolean },
): ChannelDraftIssue[] {
  if (!draft.selected) return [];
  const issues: ChannelDraftIssue[] = [];
  if (!draft.channelTitle.trim()) {
    issues.push({ connectorId: draft.connectorId, field: "channelTitle", message: "Title is required." });
  }
  if (draft.categoryId && !draft.categoryConfirmed) {
    issues.push({
      connectorId: draft.connectorId,
      field: "category",
      message: "Confirm the channel category. Suggestions are not applied until you select one.",
    });
  }
  if (opts.requireCategory && !draft.categoryId) {
    issues.push({ connectorId: draft.connectorId, field: "category", message: "Choose a channel category." });
  }
  if (draft.priceStrategy !== "inherit") {
    const n = Number(draft.priceValue);
    if (draft.priceValue.trim() === "" || !Number.isFinite(n)) {
      issues.push({ connectorId: draft.connectorId, field: "price", message: "Enter a pricing adjustment or fixed price." });
    } else {
      const price = calculatedDraftPrice(draft, opts.basePrice);
      const err = sellingPriceError(price);
      if (err) issues.push({ connectorId: draft.connectorId, field: "price", message: err });
    }
  } else {
    const err = sellingPriceError(opts.basePrice);
    if (err) issues.push({ connectorId: draft.connectorId, field: "price", message: err });
  }
  if (draft.inventoryStrategy === "fixed_cap") {
    const cap = Number(draft.inventoryCap);
    if (!Number.isFinite(cap) || cap < 0) {
      issues.push({ connectorId: draft.connectorId, field: "inventory", message: "Enter a non-negative inventory cap." });
    }
  }
  if (draft.inventoryStrategy === "percentage_allocation") {
    const pct = Number(draft.inventoryPercent);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      issues.push({ connectorId: draft.connectorId, field: "inventory", message: "Enter an allocation between 1 and 100%." });
    }
  }
  if (draft.variantMode === "selected" && draft.variantIds.length === 0) {
    issues.push({ connectorId: draft.connectorId, field: "variants", message: "Select at least one variant." });
  }
  const media = toCanonicalMediaStrategy(draft.mediaStrategy);
  if (!media || !isCanonicalMediaStrategy(media)) {
    issues.push({
      connectorId: draft.connectorId,
      field: "media",
      message: "Media strategy must be inherit (canonical gallery) or listing_override (chosen primary image).",
    });
  } else if (mediaStrategyRequiresPrimaryAsset(media) && !draft.primaryMediaAssetId) {
    issues.push({ connectorId: draft.connectorId, field: "media", message: "Choose a primary image for this channel." });
  }
  return issues;
}

export function buildDraftProviderConfig(draft: ChannelWorkspaceDraft): Record<string, unknown> {
  const pricing =
    draft.priceStrategy === "inherit"
      ? {}
      : buildPricingProviderConfig(draft.priceStrategy, draft.priceValue.trim() === "" ? null : Number(draft.priceValue));
  return {
    ...pricing,
    category: draft.categoryId
      ? { id: draft.categoryId, name: draft.categoryName || null, confirmed: draft.categoryConfirmed }
      : undefined,
    inventory:
      draft.inventoryStrategy === "fixed_cap"
        ? { cap: Number(draft.inventoryCap) }
        : draft.inventoryStrategy === "percentage_allocation"
          ? { percentage: Number(draft.inventoryPercent) }
          : undefined,
    media:
      toCanonicalMediaStrategy(draft.mediaStrategy) === "listing_override" && draft.primaryMediaAssetId
        ? { strategy: "listing_override", primary_media_asset_id: draft.primaryMediaAssetId }
        : { strategy: "inherit" },
  };
}

export type BatchCreateItemResult = {
  connectorId: string;
  ok: boolean;
  listingId?: string;
  error?: string;
  duplicate?: boolean;
};

/**
 * Create drafts independently. A validation or RPC failure on one connector
 * is recorded and does not mutate another connector's payload.
 */
export async function createSelectedChannelDrafts(
  drafts: ChannelWorkspaceDraft[],
  opts: {
    basePrice: number;
    createOne: (draft: ChannelWorkspaceDraft) => Promise<{
      success: boolean;
      listing_id?: string;
      error?: string;
      code?: string;
    }>;
  },
): Promise<{ results: BatchCreateItemResult[]; created: number; failed: number }> {
  const results: BatchCreateItemResult[] = [];
  for (const draft of drafts) {
    if (!draft.selected) continue;
    const issues = validateChannelDraft(draft, { basePrice: opts.basePrice });
    if (issues.length > 0) {
      results.push({ connectorId: draft.connectorId, ok: false, error: issues.map((i) => i.message).join(" ") });
      continue;
    }
    try {
      const res = await opts.createOne(draft);
      if (res.code === "already_exists") {
        results.push({
          connectorId: draft.connectorId,
          ok: false,
          listingId: res.listing_id,
          duplicate: true,
          error: res.error || "A draft listing already exists for this channel.",
        });
      } else if (!res.success) {
        results.push({ connectorId: draft.connectorId, ok: false, listingId: res.listing_id, error: res.error || "Failed to create draft listing." });
      } else {
        results.push({ connectorId: draft.connectorId, ok: true, listingId: res.listing_id });
      }
    } catch (e) {
      results.push({ connectorId: draft.connectorId, ok: false, error: (e as Error).message });
    }
  }
  return {
    results,
    created: results.filter((r) => r.ok).length,
    failed: results.filter((r) => !r.ok).length,
  };
}
